import { LeadAirbnbAlteration } from 'components/domain/airbnbAlterationRequest/modal/AirbnbAlteration.types';
import { LanguageCode } from '../i18n/internationalization.types';
import { BaseResponse } from './_base';
import { IntegrationEnum } from './InternalIntegrationSetting';
import { Order, OwnerCommission } from './Orders';
import { PinCodeLead } from './PinCode';
import { ReviewByHost } from './Reviews';
import { LeadQuoteOverride } from './Quote';
import { Property } from './Properties';
import { CustomDataFieldType, CustomDataFieldUsage } from './CustomDataField';

export enum LeadSource {
  ORBIRENTAL_FORM = 'ORBIRENTAL_FORM',
  ORBIRENTAL_WIDGET = 'ORBIRENTAL_WIDGET',
  ORBIRENTAL_API = 'ORBIRENTAL_API',
  OWNER_PORTAL = 'OWNER_PORTAL',
  IMPORT_AIRBNB = 'IMPORT_AIRBNB',
  IMPORT_HOMEAWAY = 'IMPORT_HOMEAWAY',
  IMPORT_VRBO = 'IMPORT_VRBO',
  IMPORT_BOOKINGCOM = 'IMPORT_BOOKINGCOM',
  IMPORT_ABRITEL = 'IMPORT_ABRITEL',
  IMPORT_TRIPADVISOR = 'IMPORT_TRIPADVISOR',
  IMPORT_OTHER_PROVIDER = 'IMPORT_OTHER_PROVIDER',
  AIRBNB_BLOCK_IMPORT = 'AIRBNB_BLOCK_IMPORT',
  DIRECT_TRIPPING = 'DIRECT_TRIPPING',
  DIRECT_HOMETOGO = 'DIRECT_HOMETOGO',
  DIRECT_BOOKINGDOTCOM = 'DIRECT_BOOKINGDOTCOM',
  DIRECT_AIRBNB = 'DIRECT_AIRBNB',
  DIRECT_EXPEDIA = 'DIRECT_EXPEDIA',
  DIRECT_HOLIDU = 'DIRECT_HOLIDU',
  DIRECT_HOMEAWAY = 'DIRECT_HOMEAWAY',
  DIRECT_FURNISHEDFINDER = 'DIRECT_FURNISHEDFINDER',
  DIRECT_HVMI = 'DIRECT_HVMI',
  DIRECT_REDAWNING = 'DIRECT_REDAWNING',
  MisterBnB = 'MisterBnB',
  ORBIRENTAL_LINKED = 'ORBIRENTAL_LINKED',
  DIRECT_GOOGLE = 'DIRECT_GOOGLE',
}

export enum LeadStatus {
  NEW = 'NEW',
  ON_HOLD = 'ON_HOLD',
  HOLD_EXPIRED = 'HOLD_EXPIRED',
  BOOKED_BY_AGENT = 'BOOKED_BY_AGENT',
  BLOCKED = 'BLOCKED',
  BOOKED_BY_CUSTOMER = 'BOOKED_BY_CUSTOMER',
  BOOKED_BY_OWNER = 'BOOKED_BY_OWNER',
  QUOTE_SENT = 'QUOTE_SENT',
  DECLINED = 'DECLINED',
  CANCELLED = 'CANCELLED',
  IGNORED = 'IGNORED',
  ARCHIVED = 'ARCHIVED',
  STAY = 'STAY',
  PAID_IN_FULL = 'PAID_IN_FULL',
  DUPLICATE = 'DUPLICATE',
  SAMPLE = 'SAMPLE',
  BOOKED_EXTERNALLY = 'BOOKED_EXTERNALLY',
  CLOSED_QUOTE = 'CLOSED_QUOTE',
  CLOSED_HOLD = 'CLOSED_HOLD',
  PENDING = 'PENDING',
  PENDING_APPROVED = 'PENDING_APPROVED',
  CANCELLED_BY_TRAVELER = 'CANCELLED_BY_TRAVELER',
  CANCELLED_BY_OWNER = 'CANCELLED_BY_OWNER',
}

export enum LeadGroupedStatus {
  RELEVANT = 'RELEVANT',
  QUOTE = 'QUOTE',
  BOOKED = 'BOOKED',
  PENDING = 'PENDING',
  DECLINED = 'DECLINED',
  STAY = 'STAY',
  CLOSED = 'CLOSED',
  CANCELLED = 'CANCELLED',
  ARCHIVED = 'ARCHIVED',
}

export enum LeadType {
  BLOCK = 'BLOCK',
  INQUIRY = 'INQUIRY',
  BOOKING = 'BOOKING',
}

export enum LeadChannel {
  HOSTFULLY = 'HOSTFULLY',
  AIRBNB = 'AIRBNB',
  BOOKING_COM = 'BOOKING_COM',
  VRBO = 'VRBO',
  HVMI = 'HVMI',
  GOOGLE = 'GOOGLE',
  FIND_RENTALS = 'FIND_RENTALS',
  GOLIGHTLY = 'GOLIGHTLY',
  HOMADS = 'HOMADS',
  HOMETOGO = 'HOMETOGO',
  TRIP_ADVISOR = 'TRIP_ADVISOR',
  VACAYMYMAY = 'VACAYMYMAY',
  HOLIDU = 'HOLIDU',
  STAYMENTITY = 'STAYMENTITY',
  GETAWAYGOGO = 'GETAWAYGOGO',
  EVERYSTAY = 'EVERYSTAY',
  EGETINNZ = 'EGETINNZ',
  REDAWNING = 'REDAWNING',
  MISTERBNB = 'MISTERBNB',
  WHIMSTAY = 'WHIMSTAY',
  NEXTPAX = 'NEXTPAX',
  RENTALS_UNITED = 'RENTALS_UNITED',
  FURNISHED_FINDER = 'FURNISHED_FINDER',
  MONAKER_GROUP = 'MONAKER_GROUP',
}

/**
 * Hostfully specific options for when the channel is LeadChannel.HOSTFULLY.
 */
export enum HostfullyLeadChannel {
  HOSTFULLY_API = 'HOSTFULLY_API',
  HOSTFULLY_DIRECT_BOOKING = 'HOSTFULLY_DIRECT_BOOKING',
  HOSTFULLY_PMP = 'HOSTFULLY_PMP',
}

export enum LeadSortingStrategy {
  CHECK_IN_DATE_ASC = 'CHECK_IN_DATE_ASC',
  CHECK_IN_DATE_DESC = 'CHECK_IN_DATE_DESC',
  CREATION_DATE_DESC = 'CREATION_DATE_DESC',
  UPDATED_AT_DESC = 'UPDATED_AT_DESC',
}

export const LeadChannelNameMap = new Map<LeadChannel, string>([
  [LeadChannel.HOSTFULLY, 'Hostfully'],
  [LeadChannel.AIRBNB, 'Airbnb'],
  [LeadChannel.BOOKING_COM, 'Booking.com'],
  [LeadChannel.VRBO, 'Vrbo'],
  [LeadChannel.HVMI, 'HVMI'],
  [LeadChannel.GOOGLE, 'Google'],
  [LeadChannel.FIND_RENTALS, 'Find Rentals'],
  [LeadChannel.GOLIGHTLY, 'GoLightly'],
  [LeadChannel.HOMADS, 'Homads'],
  [LeadChannel.HOMETOGO, 'HomeToGo'],
  [LeadChannel.TRIP_ADVISOR, 'TripAdvisor'],
  [LeadChannel.VACAYMYMAY, 'VacayMyWay'],
  [LeadChannel.HOLIDU, 'Holidu'],
  [LeadChannel.STAYMENTITY, 'Staymenity'],
  [LeadChannel.GETAWAYGOGO, 'GetawayGoGo'],
  [LeadChannel.EVERYSTAY, 'Everystay'],
  [LeadChannel.EGETINNZ, 'eGetinnz'],
  [LeadChannel.REDAWNING, 'RedAwning'],
  [LeadChannel.MISTERBNB, 'MisterBnb'],
  [LeadChannel.WHIMSTAY, 'Whimstay'],
  [LeadChannel.NEXTPAX, 'Nextpax'],
  [LeadChannel.RENTALS_UNITED, 'Rentals United'],
  [LeadChannel.FURNISHED_FINDER, 'Furnished Finder'],
  [LeadChannel.MONAKER_GROUP, 'Monaker Group'],
]);

export const ChannelToSourceMap = new Map<LeadChannel, LeadSource>([
  [LeadChannel.HOMETOGO, LeadSource.DIRECT_HOMETOGO],
  [LeadChannel.TRIP_ADVISOR, LeadSource.IMPORT_TRIPADVISOR],
  [LeadChannel.BOOKING_COM, LeadSource.DIRECT_BOOKINGDOTCOM],
  [LeadChannel.AIRBNB, LeadSource.DIRECT_AIRBNB],
  [LeadChannel.HOLIDU, LeadSource.DIRECT_HOLIDU],
  [LeadChannel.GOOGLE, LeadSource.DIRECT_GOOGLE],
  [LeadChannel.MISTERBNB, LeadSource.MisterBnB],
  [LeadChannel.REDAWNING, LeadSource.DIRECT_REDAWNING],
  [LeadChannel.HVMI, LeadSource.DIRECT_HVMI],
  [LeadChannel.VRBO, LeadSource.DIRECT_HOMEAWAY],
]);

export const LeadColor = {
  HOSTFULLY: '#FFC454',
  HOMEAWAY: '#245ABC',
  TRIP_ADVISOR: '#34E0A1',
  TRIPPING: '#F37920',
  HOMETOGO: '#612ACE',
  BOOKINGDOTCOM: '#003580',
  AIRBNB: '#ff5a5f',
  HOLIDU: '#3567B0',
  HVMI: '#F69464',
  GOOGLE: '#33A852',
  TRAVELSTAYTION: '#484848',
  GOLIGHTLY: '#5CC7D2',
  GETAWAYGOGO: '#BD2334',
  VACAYMYWAY: '#29395A',
  HOMADS: '#527CBE',
  WHIMSTAY: '#0F7081',
  FINDRENTALS: '#8BB9FE',
  OWNERDIRECT: '#E11960',
  B0ARDING: '#000000',
  SAVVY: '#1F414D',
  LAKE: '#0358FD',
  BLUEGROUND: '#273656',
  REDAWNING: '#B42D33',
  NEXTPAX: '#265886',
  RENTALS_UNITED: '#FF8700',
  DEFAULT: '#3ECB9A',
};

export const AffiliateToColorMap = new Map<IntegrationEnum, string>([
  [IntegrationEnum.HOSTFULLY, LeadColor.HOSTFULLY],
  [IntegrationEnum.ORBIRENTAL, LeadColor.HOSTFULLY],
  [IntegrationEnum.HOMEAWAY, LeadColor.HOMEAWAY],
  [IntegrationEnum.TRIPADVISOR, LeadColor.TRIP_ADVISOR],
  [IntegrationEnum.HOMETOGO, LeadColor.HOMETOGO],
  [IntegrationEnum.BOOKING_COM, LeadColor.BOOKINGDOTCOM],
  [IntegrationEnum.AIRBNB, LeadColor.AIRBNB],
  [IntegrationEnum.HOLIDU_COM, LeadColor.HOLIDU],
  [IntegrationEnum.HVMI, LeadColor.HVMI],
  [IntegrationEnum.GOOGLE, LeadColor.GOOGLE],
  [IntegrationEnum.TRAVELSTAYTION, LeadColor.TRAVELSTAYTION],
  [IntegrationEnum.GOLIGHTLY, LeadColor.GOLIGHTLY],
  [IntegrationEnum.GETAWAYGOGO, LeadColor.GETAWAYGOGO],
  [IntegrationEnum.VACAYMYWAY, LeadColor.VACAYMYWAY],
  [IntegrationEnum.HOMADS, LeadColor.HOMADS],
  [IntegrationEnum.WHIMSTAY, LeadColor.WHIMSTAY],
  [IntegrationEnum.FINDRENTALS, LeadColor.FINDRENTALS],
  [IntegrationEnum.OWNERDIRECT, LeadColor.OWNERDIRECT],
  [IntegrationEnum.B0ARDING, LeadColor.B0ARDING],
  [IntegrationEnum.LAKE, LeadColor.LAKE],
  [IntegrationEnum.BLUEGROUND, LeadColor.BLUEGROUND],
  [IntegrationEnum.REDAWNING, LeadColor.REDAWNING],
  [IntegrationEnum.NEXTPAX, LeadColor.NEXTPAX],
  [IntegrationEnum.RENTALS_UNITED, LeadColor.RENTALS_UNITED],
  [IntegrationEnum.BNBFINDER, LeadColor.SAVVY],
]);

export enum LeadCircleColor {
  GREEN = 'GREEN',
  ORANGE = 'ORANGE',
  RED = 'RED',
  BLUE = 'BLUE',
}

export enum LeadAvailablePipelineAction {
  ACCEPT_PENDING = 'ACCEPT_PENDING',
  AIRBNB_PRE_APPROVE = 'AIRBNB_PRE_APPROVE',
  AIRBNB_REVIEW_ALTERATION = 'AIRBNB_REVIEW_ALTERATION',
  ASSIGN_TO_ME = 'ASSIGN_TO_ME',
  CANCEL_ALTERATION_REQUEST = 'CANCEL_ALTERATION_REQUEST',
  CANCEL_BOOKING = 'CANCEL_BOOKING',
  CHANGE_BOOKING = 'CHANGE_BOOKING',
  CLOSE_LEAD = 'CLOSE_LEAD',
  DECLINE_PENDING = 'DECLINE_PENDING',
  EXTEND_HOLD = 'EXTEND_HOLD',
  IGNORE_LEAD = 'IGNORE_LEAD',
  MARK_AS_BOOKED = 'MARK_AS_BOOKED',
  MARK_AS_PAID = 'MARK_AS_PAID',
  OPEN_INBOX = 'OPEN_INBOX',
  PLACE_ON_HOLD = 'PLACE_ON_HOLD',
  REBOOK = 'REBOOK',
  REBOOK_AS_NEW_LEAD = 'REBOOK_AS_NEW_LEAD',
  RECORD_PAYMENT = 'RECORD_PAYMENT',
  REOPEN = 'REOPEN',
  REVIEW = 'REVIEW',
  REVOKE_HOLD = 'REVOKE_HOLD',
  SEND_ALTERNATIVE_OPTIONS = 'SEND_ALTERNATIVE_OPTIONS',
  SEND_BALANCE_REQUEST = 'SEND_BALANCE_REQUEST',
  SEND_CHECK_IN_INSTRUCTIONS = 'SEND_CHECK_IN_INSTRUCTIONS',
  SEND_MINIMUM_STAY_NOTE = 'SEND_MINIMUM_STAY_NOTE',
  SEND_PAYMENT_REMINDER = 'SEND_PAYMENT_REMINDER',
  SEND_PRE_ARRIVAL_FORM = 'SEND_PRE_ARRIVAL_FORM',
  SEND_QUOTE = 'SEND_QUOTE',
  SEND_REMINDER = 'SEND_REMINDER',
}

export enum LeadPipelineAction {
  ACCEPT_PENDING = 'ACCEPT_PENDING',
  CANCEL_BOOKING = 'CANCEL_BOOKING',
  CLOSE_LEAD = 'CLOSE_LEAD',
  DECLINE_PENDING = 'DECLINE_PENDING',
  EXTEND_HOLD = 'EXTEND_HOLD',
  IGNORE_LEAD = 'IGNORE_LEAD',
  MARK_AS_BOOKED = 'MARK_AS_BOOKED',
  PLACE_ON_HOLD = 'PLACE_ON_HOLD',
  PRE_APPROVE = 'PRE_APPROVE',
  REBOOK = 'REBOOK',
  REOPEN = 'REOPEN',
  REVOKE_HOLD = 'REVOKE_HOLD',
  SEND_QUOTE = 'SEND_QUOTE',
  SEND_CHECK_IN_INSTRUCTIONS = 'SEND_CHECK_IN_INSTRUCTIONS',
}

export enum LeadCreatorType {
  EMPLOYEE = 'EMPLOYEE',
  OWNER = 'OWNER',
}

export enum LeadBookingCancellationOrigin {
  HOST = 'HOST',
  GUEST = 'GUEST',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
}

export interface LeadVrboMetadata {
  offlineLead: boolean | null;
}

export interface Lead {
  uid: string;
  address?: string;
  address2?: string;
  affiliate: IntegrationEnum | null;
  assignee: LeadAssignee | null;
  availablePipelineActions?: LeadAvailablePipelineAction[];
  bookedDate?: Date;
  bookedDateTimeString?: string | null;
  cellPhoneNumber?: string;
  channel?: LeadChannelTO;
  city?: string;
  countdown?: LeadCountdown;
  countryCode?: string;
  creator?: LeadCreator | null;
  order?: Order;
  ownerCommission?: OwnerCommission;
  property: Partial<Property>;
  source: LeadSource;
  status: LeadStatus;
  checkInLocalDateTime: string;
  checkOutLocalDateTime: string;
  checkInDate?: Date;
  checkOutDate?: Date;
  arrivalLocalDateTime?: string;
  vrboMetadata?: LeadVrboMetadata | null;
  departureLocalDateTime?: string;
  arrivalDate?: Date;
  departureDate?: Date;
  adultCount: number;
  childrenCount: number;
  infantCount: number;
  petCount: number;
  phoneNumber?: string;
  preferredLocale?: LanguageCode;
  agreementSigned: boolean;
  autoRefundFail?: boolean;
  autoRefundOk?: boolean;
  hasCompletedPreArrivalForm: boolean;
  wereCheckInInstructionsSent: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  leadType?: LeadType;
  createdDate?: Date;
  createdDateTimeString?: string;
  paidInFull?: boolean;
  referrer?: string | null;
  extraGuests?: {
    id?: number;
    firstName: string;
    lastName: string;
    email: string;
    age: number;
    phoneNumber?: string;
    hasIdentificationDocument?: boolean;
    passportCountry?: string;
    passportID?: string;
  }[];
  willHaveParty?: boolean;
  willHaveVisit?: boolean;
  passportID?: string;
  passportCountryCode?: string;
  guest?: any;
  customData?: {
    uid: string;
    name: string;
    usage: CustomDataFieldUsage;
    type: CustomDataFieldType;
    value: {
      text: string;
      longText: string;
    };
  }[];
  notes?: string;
  extraNotes?: string;
  flightNumber?: string;
  pinCode?: PinCodeLead;
  reviewByHost?: ReviewByHost;
  reasonForTrip?: string;
  secondaryEmail?: string;
  state?: string;
  zipCode?: string;
  creationMetadata?: string;
  externalBookingID?: string;
  usePreAuthForDeposits?: boolean;
  wasProcessed?: boolean;
  wasSecurityDepositReceived?: boolean;
  wasSecurityDepositReturned?: boolean;
  riskScore?: number;
  fieldsPermissionsMetadata?: {
    fieldName: string;
    metadata: {
      editable: boolean;
    };
  }[];
  unitUid?: string;
  hotel?: {
    uid: string;
    name: string;
  };
  bookingDotComData?: {
    bookingDotComId?: string;
    bookingDotComRoomId?: string;
    rateId?: string;
    rateName?: string;
    reservationId?: string;
    roomReservationId?: string;
  };
  checkInDateTimeString?: string | null;
  checkInUtcDateTime?: string;
  checkOutUtcDateTime?: string;
  checkInUtcDate?: Date;
  checkOutUtcDate?: Date;
  duplicateOfLeadUid?: string;
  hotelUid?: string;
  groupUid?: string;
  isGroupBooking?: boolean;
  unit?: {
    uid: string;
    name: string;
  };
  airbnbAlteration?: LeadAirbnbAlteration;
  age?: number;
}

export interface LeadAssignee {
  uid: string;
  bestProfile: string;
  picture?: string;
}

export interface LeadChannelTO {
  bestProfile: string;
  channel: LeadChannel;
}

export interface LeadCountdown {
  color: LeadCircleColor;
  countdownMinutes?: number;
  countdownPercentage?: number;
  overdue?: boolean;
  visualCountdownValue?: string;
}

export interface LeadCreator {
  type: LeadCreatorType;
}

export interface GetAllLeadChannelsResponseTO extends BaseResponse {
  leadChannels: LeadChannelTO[];
}

export interface GetLeadsGraphQLResponseTO {
  data: {
    leads: Lead[];
  };
}

export interface LeadSavingPayload {
  address?: string;
  address2?: string;
  adultCount: number;
  arrivalLocalDateTime: string;
  assigneeUid?: string;
  attributedChannel?: IntegrationEnum | null;
  cellPhoneNumber?: string;
  checkInDate: string;
  checkOutDate: string;
  childrenCount: number;
  city?: string;
  countryCode: string;
  departureLocalDateTime: string;
  email: string;
  extraNotes?: string;
  firstName?: string;
  flightNumber?: string;
  infantCount: number;
  lastName?: string;
  leadType: LeadType;
  notes?: string;
  overrides?: LeadQuoteOverride;
  petCount: number;
  phoneNumber?: string;
  preferredLocale: string;
  propertyUid: string;
  reasonForTrip?: string;
  secondaryEmail?: string;
  source: LeadSource;
  state?: string;
  status: LeadStatus;
  zipCode?: string;
}

export interface LeadSavingResult {
  uid: string;
  propertyUid: string;
  checkInLocalDateTime: string;
  checkOutLocalDateTime: string;
  checkInDate?: Date;
  checkOutDate?: Date;
  checkInUtcDateTime?: string;
  checkOutUtcDateTime?: string;
  checkInUtcDate?: Date;
  checkOutUtcDate?: Date;
  source: LeadSource;
  status: LeadStatus;
  // define more properties that are coming from the backend if required (com.orbirental.api.v2.TOs.LeadApiTO)
}

export interface LeadGuestInformation {
  firstName: string;
  lastName: string;

  address1: string;
  address2: string;
  zipCode: string;
  city: string;
  state: string;
  countryCode: string;

  passportId: string;
  passportCountryCode: string;

  adultCount: number;
  childrenCount: number;
  petCount: number;
  infantCount: number;
  riskScore: number;

  email: string;
  secondaryEmail: string;
  phoneNumber: string;
  cellPhoneNumber: string;

  flightNumber: string;
  reasonForTrip: string;
  willHaveParty: boolean;
  willHaveVisit: boolean;

  preferredCurrency: string;
  preferredLocale: string;

  arrivalLocalDateTime: string;
  departureLocalDateTime: string;
}

export interface LeadUpdateInternalPayload {
  propertyUid?: string;
  unitUid?: string;
  checkInLocalDateTime?: string;
  checkOutLocalDateTime?: string;
  checkInLocalDate?: string;
  checkOutLocalDate?: string;
  status: LeadStatus;
  assigneeUid?: string;
  attributedChannel?: IntegrationEnum | null;
  guestInformation?: LeadGuestInformation;
  notes?: string;
  extraNotes?: string;
  quoteOverrides?: LeadQuoteOverride;
}

export interface LeadInternal {
  uid: string;
  propertyUid: string;
  unitUid: string;
  checkInLocalDateTime: string;
  checkOutLocalDateTime: string;
  checkInLocalDate: string;
  checkOutLocalDate: string;
  status: LeadStatus;
  type: LeadType;
  source: LeadSource;
  bookedUtcDateTime: string;
  externalBookingId: string;
  notes: string;
  extraNotes: string;
  assignee: {
    uid: string;
    type: LeadCreatorType;
  };
  metadata: {
    createdUtcDateTime: string;
    updatedUtcDateTime: string;
    creationCircumstances: string;
  };
  guestInformation: LeadGuestInformation;
}

export interface LeadInternalResponse {
  lead?: LeadInternal;
  apiErrorMessage?: string;
}
